const asFedVsDryMatter = {
  slug: "as-fed-vs-dry-matter",
  title:
    "Understand the difference between As Fed and Dry Matter basis",
  createdDate: "2026-04-09",
  updatedDate: null,
  author: "The Picky Kitty",
  imageKey: "as_fed_dry_matter.png",
  coverImageAlt: "As fed versus dry matter illustration",
  summary:
    "As Fed numbers can be misleading when comparing wet and dry foods. Learn how Dry Matter helps you compare food options more fairly, with simple examples and practical conversion tips.",
  category: "Learn more",
  sections: [
    {
      heading: "Why these terms matter",
      paragraphs: [
        "Cat food labels are packed with numbers, but those numbers do not always describe nutrients in the same way. If you compare foods without checking the basis of calculation, it is very easy to choose the wrong product for your cat's needs.",
        "One of the most common points of confusion is As Fed versus Dry Matter. Understanding this one distinction already improves the quality of food comparisons a lot.",
        // "On The Picky Kitty, we present both views whenever possible so you can compare products with better context instead of relying on one number alone.",
      ],
    },
    {
      heading: "As Fed vs Dry Matter Basis (DMB)",
      paragraphs: [
        "As Fed values are nutrient percentages in the food as sold, including water. This is what appears on most labels.",
        "Dry Matter Basis (often referred to as DMB) removes moisture from the equation. Because wet and dry foods contain very different water levels, DMB is the better method when comparing products across formats.",
        "Simple example:",
        "• Wet food: 10% protein As Fed, 80% moisture -> about 50% protein on a Dry Matter basis.",
        "• Dry food: 30% protein As Fed, 10% moisture -> about 33% protein on a Dry Matter basis.",
        {
          text: "From As Fed values alone, it looks like the dry food has a much higher protein level (30% vs 10%). But once you compare on a Dry Matter basis, ",
          boldText: "reality is different:",
          afterText:
            " the wet food is about 50% protein and the dry food about 33%, so the dry food is not actually higher. The conversion formula is explained in the next section.",
        },
        "Comparing As Fed values for products with very different moisture levels is like comparing apples and oranges.",
        "Typical moisture ranges are very different by format: dry foods are often around 6-12% moisture, while wet foods are commonly around 70-85%.",
        "Even within the same format, moisture can still vary from one product to another. That means As Fed values alone are not reliable for accurate comparisons, whether you compare wet vs wet, dry vs dry, or wet vs dry.",
      ],
    },
    {
      heading: "The conversion formula",
      paragraphs: [
        "Fortunately, there is a simple way to convert As Fed values to Dry Matter, as long as the moisture level is known (it is often listed directly on the food packaging).",
        {
          text: "Dry Matter % = (As Fed % / (100 - moisture %)) x 100",
          isFormula: true,
        },
        "Example: if protein is 10% As Fed and moisture is 80%, Dry Matter protein is (10 / 20) x 100 = 50%.",
        "This makes wet and dry foods comparable on the same moisture-free basis.",
      ],
    },
    {
      heading: "How to use both in practice",
      paragraphs: [
        "1) Read the As Fed values from the label and check moisture.",
        "2) Convert key nutrients (protein, phosphorus, fat, carbs) to Dry Matter.",
        "3) Compare foods using Dry Matter values, especially across wet and dry formats.",
        "This approach reduces false comparisons and helps you make more accurate choices.",
      ],
    },
    {
      heading: "Using The Picky Kitty to convert values quickly",
      paragraphs: [
        "To support fair comparisons and save you the conversion work, The Picky Kitty shows Dry Matter values in the database and in product cards. As Fed values are shown on each product page alongside Dry Matter values, so you can see both the label view and the moisture-adjusted view.",
        "The goal is not to market one format over another, but to reduce comparison errors and help you interpret labels more accurately. The Picky Kitty is your guide to make sure you are comparing apples to apples.",
        {
          text: "If you need a quick conversion, use the ",
          linkText: "Dry Matter Conversion tool",
          linkTo: "/Tools?tab=dry-matter-conversion",
          afterText:
            " and select the Dry Matter Conversion tab. It helps you move from As Fed to Dry Matter consistently when labels provide moisture.",
        },
      ],
    },
  ],
};

export default asFedVsDryMatter;
